import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import {
    ClientCustomRole,
    ClientInterface,
    Employee,
} from '../../../shared/interfaces/client-data';
import { ClientsService } from '../../../shared/services/clients.service';
import { UserCreateModel } from '../../interfaces/user';
import { UpdateUser, UserService } from '../../../shared/services/user.service';
import { EmployeeService } from '../../../shared/services/employee.service';
import { GridComponent, GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { filterBy, orderBy, SortDescriptor, State } from '@progress/kendo-data-query';
import { ResetPasswordService } from '../../../shared/services/reset-password.service';
import { ToastService } from '../../../shared/services/toast.service';
import { UserPermissionsService } from '../../services/user-permissions.service';
import {
    NoDataActionType,
    NoDataSourceType,
    NoDataTypes,
} from '../../../shared/interfaces/no-data';
import { LimegearUserModel } from '../../../shared/classes/limegearUser.model';
import { ConfirmationAlertDialogComponent } from '../confirmation-alert-dialog/confirmation-alert-dialog.component';
import { UserPasswordResetRequest } from 'app/modules/shared/classes/user-password-reset';
import { BehaviorSubject } from 'rxjs';
import { EmailConfirmationDialogComponent } from 'app/modules/shared/components/email-confirmation-dialog/email-confirmation-dialog.component';
import { CurrentUserStateService } from '../../../shared/services/current-user-state.service';
import { ImpersonationService } from '../../../shared/services/impersonation.service';
import { ReusableFunctions } from '../../../shared/classes/reusable-functions';
import { ExcelExportData } from '@progress/kendo-angular-excel-export';
import { MatDialog } from '@angular/material/dialog';
import { WelcomeEmailSendStatus } from 'app/modules/shared/interfaces/client-data';

@Component({
    selector: 'app-user-management',
    templateUrl: './user-management.component.html',
    styleUrls: ['./user-management.component.scss'],
})
export class UserManagementComponent implements OnInit {
    editUserForm: UntypedFormGroup;
    clients: ClientInterface[];
    filteredClients: ClientInterface[];
    clientSearchTerm: string;
    employeeSearchTerm: string;
    employees: Employee[];
    empEmailsAvailable: Employee[];
    filteredEmpEmailsAvailable: Employee[];
    selectedEmployee: BehaviorSubject<Employee>;
    filteredEmployees: Employee[];
    user: UpdateUser;
    originalUser: UpdateUser;
    employeeIdentifier: string;
    employeeGridView: GridDataResult;
    clientGridView: GridDataResult;
    info = true;
    type: 'numeric' | 'input' = 'input';
    pageSizes = true;
    previousNext = true;
    pageSize = 10;
    skip = 0;
    mySelection: number[] = [];
    clientSort: SortDescriptor[] = [{ field: 'clientFullName', dir: 'asc' }];
    employeeSort: SortDescriptor[] = [{ field: 'lastName', dir: 'asc' }];
    stepNumber: number;
    isSuperUser: boolean;
    showSelectEmployeeStep: boolean;
    newPassword: string;
    clientShortName: string;
    clientCustomRoles: ClientCustomRole[];
    clientCustomRolesNoMinMax: ClientCustomRole[];
    clientPermissionSets: any[];
    defaultPositionValues: any;
    onFinalStep: boolean;
    loadingEmployees: boolean = true;
    useSSO?: boolean;
    extendAccessMenu = false;
    noDataType: NoDataTypes;
    noEmployees: boolean;
    currentUser: LimegearUserModel;
    state: State = {
        filter: { filters: [], logic: 'and' },
    };
    readyToEmail: boolean = false;
    emailAllCheckbox: boolean = false;
    currentDate: Date;
    emails: UserPasswordResetRequest[] = [];
    multipleEmails: boolean = false;
    exportFileName: string;
    exportData: Employee[];
    canCreateUser: boolean = false;

    activeUserFilter = 1; // Defaults to Active

    activeUserFilterOptions = [
        { label: 'All', value: 0 },
        { label: 'Active', value: 1 },
        { label: 'Inactive', value: 2 },
    ];

    constructor(
        private formBuilder: UntypedFormBuilder,
        private userService: UserService,
        private clientService: ClientsService,
        private employeeService: EmployeeService,
        private resetPasswordService: ResetPasswordService,
        private toastService: ToastService,
        private permissionService: UserPermissionsService,
        public dialog: MatDialog,
        private currentUserStateService: CurrentUserStateService,
        private _reusableFunctions: ReusableFunctions,
        private impersonationService: ImpersonationService
    ) {
        this.editUserForm = this.createFormGroup();
        this.selectedEmployee = new BehaviorSubject<Employee>(null);
        this.getExcelExportData = this.getExcelExportData.bind(this);
    }

    ngOnInit() {
        scroll(0, 0);
        this.noDataType = {
            action: NoDataActionType.pageStart,
            searchTerm: '',
            componentTitle: 'employees',
            inputSource: NoDataSourceType.chartCard,
        };
        this.currentDate = new Date();
        this.setAuthenticationMethod();
        this.onFinalStep = false;
        this.stepNumber = 1;
        this.showSelectEmployeeStep = true;
        this.getAccessLevelsForClient();
        this.getPermissionSetsForClient();
        this.getEmployeesForClient();
    }
    setAuthenticationMethod() {
        this.clientService.getAuthenticationMethod().subscribe(
            (useSSO) => {
                this.useSSO = useSSO;
            },
            (error) => {
                throw new Error(error);
            }
        );
    }

    getEmployeesForClient(): void {
        this.loadingEmployees = true;
        this.employeeService.getAllEmployees().subscribe(
            (employees) => {
                employees.forEach((employee) => {
                    if (!employee.isClientAdmin) employee.isClientAdmin = false;
                });
                if (employees.length) {
                    this.employees = employees;
                    this.loadEmployeesIntoGridView(true);
                    this.getAvailableToEmail();
                    if (this.selectedEmployee.value) {
                        this.selectedEmployee.next(
                            employees.find(
                                (emp) =>
                                    emp.primaryIdentifier ===
                                    this.selectedEmployee.value.primaryIdentifier
                            )
                        );
                    }
                } else {
                    this.noEmployees = true;
                }
                this.loadingEmployees = false;

                this.filterEmployees();
            },
            (error) => {
                this.loadingEmployees = false;
                throw new Error(error);
            }
        );
    }

    getAvailableToEmail() {
        // Do we care that an employee with an Employment Status of Terminated could get this email?
        for (let emp of this.employees) {
            emp.welcomeEmailStatus = WelcomeEmailSendStatus.Unknown;
            if (emp.employeeStatus.toUpperCase() === 'TERMINATED') {
                emp.welcomeEmailStatus = WelcomeEmailSendStatus.EmployeeTerminated;
            } else if (emp.lastLoginDate && emp.isUser) {
                emp.welcomeEmailStatus = WelcomeEmailSendStatus.Delivered;
            } else if (emp.isUser && !emp.welcomeEmailExpirationDate) {
                emp.welcomeEmailStatus = WelcomeEmailSendStatus.NotSent;
            } else if (
                emp.isUser &&
                emp.welcomeEmailExpirationDate &&
                this.currentDate.getTime() >= new Date(emp.welcomeEmailExpirationDate).getTime()
            ) {
                emp.welcomeEmailStatus = WelcomeEmailSendStatus.Expired;
            } else if (
                emp.isUser &&
                emp.welcomeEmailExpirationDate &&
                this.currentDate.getTime() < new Date(emp.welcomeEmailExpirationDate).getTime()
            ) {
                emp.welcomeEmailStatus = WelcomeEmailSendStatus.Pending;
            }
        }
        this.empEmailsAvailable = this.employees.filter(
            (emp) =>
                emp.welcomeEmailStatus === WelcomeEmailSendStatus.Pending ||
                emp.welcomeEmailStatus === WelcomeEmailSendStatus.Expired ||
                emp.welcomeEmailStatus === WelcomeEmailSendStatus.NotSent
        );
        this.filteredEmpEmailsAvailable = this.empEmailsAvailable;
        this.updateButtonAndCheckAll();
    }

    checkAll() {
        if (this.filteredEmpEmailsAvailable?.length) {
            this.emailAllCheckbox = !this.emailAllCheckbox;
            this.readyToEmail = this.emailAllCheckbox;
            this.multipleEmails = this.emailAllCheckbox;

            this.filteredEmpEmailsAvailable.forEach((emp) => {
                emp.sendEmail = this.emailAllCheckbox;
            });
        }
    }

    createFormGroup() {
        return this.formBuilder.group({
            user: this.formBuilder.group(new UserCreateModel()),
        });
    }

    // ... Employee page functions
    employeeSortChange(sort: SortDescriptor[]): void {
        this.employeeSort = sort;
        this.employeeGrid();
    }

    employeeGrid() {
        let orderedData = orderBy(
            this.filteredEmployees ? this.filteredEmployees : this.employees,
            this.employeeSort
        );

        this.setGridView(orderedData);
    }

    setGridView(orderedData: Employee[]) {
        this.exportData = orderedData.map((obj) => ({ ...obj }));
        this.employeeGridView = {
            data: orderedData.slice(this.skip, this.skip + this.pageSize),
            total: orderedData.length,
        };
    }

    employeePageChange({ skip, take }: PageChangeEvent): void {
        this.skip = skip;
        this.pageSize = take;
        this.loadEmployeesIntoGridView();
    }

    filterEmployees(): void {
        if (this.loadingEmployees || this.noEmployees) return;
        if (!this.employees || this.employees.length === 0) return;

        this.filteredEmployees = [...this.employees];
        let searchTermTokens = [];

        if (this.activeUserFilter !== 0) {
            this.filteredEmployees = this.filteredEmployees.filter((employee) =>
                this.activeUserFilter === 1 ? employee.isActive : !employee.isActive
            );
        }

        if (this.employeeSearchTerm && this.employeeSearchTerm.length > 0) {
            searchTermTokens = this.employeeSearchTerm.split(' ');
            this.filteredEmployees = filterBy(this.filteredEmployees, {
                logic: 'or',
                filters: [
                    {
                        field: 'firstName',
                        operator: 'startswith',
                        value: searchTermTokens[0],
                        ignoreCase: true,
                    },
                    {
                        field: 'lastName',
                        operator: 'startswith',
                        value: searchTermTokens[0],
                        ignoreCase: true,
                    },
                    {
                        field: 'lfName',
                        operator: 'startswith',
                        value: searchTermTokens[0],
                        ignoreCase: true,
                    },
                    {
                        field: 'jobTitle',
                        operator: 'contains',
                        value: searchTermTokens[0],
                        ignoreCase: true,
                    },
                    {
                        field: 'primaryIdentifier',
                        operator: 'contains',
                        value: searchTermTokens[0],
                        ignoreCase: true,
                    },
                    {
                        field: 'isUser',
                        operator: 'eq',
                        value: searchTermTokens[0].toLowerCase() === 'yes' ? true : '',
                        ignoreCase: true,
                    },
                ],
            });
        }

        if (searchTermTokens.length > 1) {
            this.filteredEmployees = filterBy(this.filteredEmployees, {
                logic: 'or',
                filters: [
                    {
                        field: 'firstName',
                        operator: 'startswith',
                        value: searchTermTokens[1],
                        ignoreCase: true,
                    },
                    {
                        field: 'lastName',
                        operator: 'startswith',
                        value: searchTermTokens[1],
                        ignoreCase: true,
                    },
                    {
                        field: 'lfName',
                        operator: 'startswith',
                        value: searchTermTokens[1],
                        ignoreCase: true,
                    },
                    {
                        field: 'jobTitle',
                        operator: 'startswith',
                        value: searchTermTokens[1],
                        ignoreCase: true,
                    },
                ],
            });
        }

        let orderedData = orderBy(this.filteredEmployees, this.employeeSort);
        this.setGridView(orderedData);
        this.filterEmailsAvailable();
    }

    public onStateChange(state: State) {
        this.state = state;
    }

    checkChange(id: string) {
        this.selectedEmployee.next(this.employees.find((emp) => emp.primaryIdentifier === id));
        this.selectedEmployee.value.sendEmail = !this.selectedEmployee.value.sendEmail;
        this.updateButtonAndCheckAll();
    }

    updateButtonAndCheckAll() {
        if (this.filteredEmpEmailsAvailable?.length) {
            let anyFalse =
                this.filteredEmpEmailsAvailable.findIndex((emp) => !emp.sendEmail) !== -1
                    ? true
                    : false;
            let anyTrue =
                this.filteredEmpEmailsAvailable.findIndex((emp) => emp.sendEmail) !== -1
                    ? true
                    : false;
            if (this.emailAllCheckbox && anyFalse) this.emailAllCheckbox = false;
            else if (!anyFalse) this.emailAllCheckbox = true;

            if (!this.readyToEmail && anyTrue) this.readyToEmail = true;
            else if (this.readyToEmail && !anyTrue) this.readyToEmail = false;

            if (this.filteredEmpEmailsAvailable.filter((emp) => emp.sendEmail).length > 1)
                this.multipleEmails = true;
            else this.multipleEmails = false;
        } else {
            this.readyToEmail = false;
            this.emailAllCheckbox = false;
        }
    }

    updateSendStatus() {
        this.filteredEmpEmailsAvailable.forEach((emp, index) => {
            if (this.selectedEmployee.value.primaryIdentifier === emp.primaryIdentifier) {
                this.filteredEmpEmailsAvailable[index].sendEmail = true;
            } else {
                this.filteredEmpEmailsAvailable[index].sendEmail = false;
            }
        });
        this.openChangeClientDialog();
    }

    filterEmailsAvailable() {
        if (this.filterEmployees) {
            this.filteredEmpEmailsAvailable = this.empEmailsAvailable?.filter((emp) =>
                this.filteredEmployees.includes(emp)
            );
        } else {
            this.filteredEmpEmailsAvailable = this.empEmailsAvailable;
        }
        this.updateButtonAndCheckAll();
    }

    sendEmail() {
        let emails = [];

        this.filteredEmpEmailsAvailable
            .filter((emp) => emp.sendEmail)
            .forEach((emp) => emails.push({ username: emp.loginEmail }));

        this.userService.sendWelcomeEmail(emails).subscribe(
            () => {
                this.getEmployeesForClient();
            },
            (error) => {
                throw new Error(error);
            }
        );
    }

    loadEmployeesIntoGridView(showAll: boolean = false) {
        if (showAll) {
            this.skip = 0;
        }
        let orderedData = orderBy(
            this.filteredEmployees && !showAll ? this.filteredEmployees : this.employees,
            this.employeeSort
        );
        this.setGridView(orderedData);
    }

    employeeSelectionChangeHandler(event: any): void {
        this.employeeIdentifier = event.selectedRows[0].dataItem.primaryIdentifier;
        this.showSelectEmployeeStep = false;
        this.stepNumber++;
        this.prepareFinalStep();
    }

    // ... "Step" functions
    goToPreviousStep() {
        this.clientSearchTerm = this.employeeSearchTerm = '';
        if (this.employeeIdentifier) {
            this.employeeIdentifier = undefined;
            this.showSelectEmployeeStep = true;
            this.loadEmployeesIntoGridView(true);
        }

        this.user = undefined;
        this.newPassword = undefined;
        this.stepNumber--;
        this.onFinalStep = false;
        this.mySelection = [];
    }

    async prepareFinalStep() {
        this.onFinalStep = true;
        this.selectedEmployee.next(
            this.employees.find(
                (employee) => employee.primaryIdentifier === this.employeeIdentifier
            )
        );
        if (this.selectedEmployee.value.userGUID) {
            this.prepareEditUserStep();
        } else {
            await this.prepareCreateUserStep();
        }
    }

    async prepareCreateUserStep() {
        if (this.selectedEmployee?.value?.loginEmail) {
            const emailAddressCheck = {
                emailAddress: this.selectedEmployee?.value?.loginEmail,
            };
            let data = await this.userService.isLoginEmailAvailable(emailAddressCheck).toPromise();
            this.canCreateUser = data;
        }
    }

    prepareEditUserStep() {
        this.userService
            .getUserByUsername(this.selectedEmployee.value.loginEmail)
            .subscribe((user) => {
                if (!user) {
                    throw new Error(
                        'Could not find user with loginEmail: ' +
                            this.selectedEmployee.value.loginEmail
                    );
                }

                this.user = user;

                if (!this.user.permissionSetName) {
                    this.user.permissionSetName = 'None';
                }

                if (this.user?.alternateLoginEmail) {
                    this.user.hasAlternateLoginEmail = true;
                } else {
                    this.user.hasAlternateLoginEmail = false;
                    this.user.alternateLoginEmail = this.user.loginEmail;
                }

                this.originalUser = JSON.parse(JSON.stringify(this.user));

                this.getPositionDefaults();
            });
    }

    createUser() {
        if (
            this.currentUserStateService.isLBASuperUser() &&
            this.impersonationService.isImpersonatingClient()
        )
            this.clientShortName = this.impersonationService.impersonateClient;
        else {
            this.clientShortName = this.currentUserStateService.currentUser?.clientShortName;
        }

        const newUser: UserCreateModel = {
            loginEmail: this.selectedEmployee.value.loginEmail,
            clientShortName: this.clientShortName,
            employeeIdentifier: this.selectedEmployee.value.primaryIdentifier,
            positionName: this.selectedEmployee.value.positionName,
        };

        this.userService.createUser(newUser).subscribe((response) => {
            if (response === true) {
                this.toastService.success('User has been created.');
                this.getEmployeesForClient();
                this.prepareEditUserStep();
            } else {
                this.toastService.warning('Error creating new user.');
            }
        });
    }

    getAccessLevelsForClient() {
        this.clientService.getCustomRoles().subscribe((roles) => {
            if (!roles) {
                throw new Error('Client has not yet setup their custom role levels.');
            } else {
                this.clientCustomRoles = roles;

                // Make deep copy minus max and min role to determine if user can extend access
                this.clientCustomRolesNoMinMax = JSON.parse(JSON.stringify(this.clientCustomRoles));
                this.clientCustomRolesNoMinMax.shift(); // Remove first element (Corp)
                this.clientCustomRolesNoMinMax.pop(); // Remove last element (Emp)
            }
        });
    }

    getPermissionSetsForClient() {
        this.permissionService.getPermissionSets().subscribe((data) => {
            if (data) {
                this.clientPermissionSets = data;
                this.clientPermissionSets.unshift({
                    permissionSetKey: 0,
                    clientKey: 0,
                    name: 'None',
                    description: null,
                });
            }
        });
    }

    getPositionDefaults() {
        this.permissionService.getPositionDefaults(this.user.positionName).subscribe((data) => {
            if (data) {
                this.defaultPositionValues = data;
            }
        });
    }

    editExistingUser(): void {
        if (this.user.customPermissionSetKey) {
            this.user.customPermissionSetKey = +this.user.customPermissionSetKey;
        }
        this.userService.editUser(this.user).subscribe(
            (response) => {
                if (response === true) {
                    this.toastService.success('User has been successfully updated.');
                    this.getEmployeesForClient();
                    this.prepareEditUserStep();
                }
            },
            (error) => {
                if (error.status == 409) {
                    this.toastService.warning(error.error.error);
                } else {
                    throw new Error(error);
                }
            }
        );
    }

    resetPassword(): void {
        this.resetPasswordService.requestPasswordReset(this.user.loginEmail).subscribe(
            () => {
                this.toastService.success('Password Reset Email has been sent.');
            },
            (error) => {
                throw new Error(error);
            }
        );
    }

    // Extended Access Menu
    extendedAccessMenuToggle() {
        this.extendAccessMenu = !this.extendAccessMenu;
    }

    extendedAccessMenuCanceled(event: string) {
        this.extendedAccessMenuToggle();

        if (event) {
            this.toastService.warning(event);
        }
    }

    extendedAccessMenuSaved() {
        this.extendedAccessMenuToggle();
        this.toastService.success('Extended access has been successfully saved!');
    }
    // End Extended Access Menu

    saveDisabled(): boolean {
        if (!this.user || !this.originalUser) {
            return true;
        }

        const u = this.user;
        const ou = this.originalUser;

        const powerUserMatch = u.isClientAdmin === ou.isClientAdmin;
        const defaultAccessMatch =
            u.hasDefaultAccess === ou.hasDefaultAccess &&
            u.hasDefaultPermissions === ou.hasDefaultPermissions;
        const enabledMatch = u.isDisabled === ou.isDisabled;
        const permissionsMatch = u.permissionSetName === ou.permissionSetName;
        const accessMatch = u.clientCustomRoleName === ou.clientCustomRoleName;
        const hasGLMatch = u.hasGLAccess === ou.hasGLAccess;
        const clientOwnerMatch = u.isClientOwner === ou.isClientOwner;
        let alternateEmailMatch =
            u.alternateLoginEmail === ou.alternateLoginEmail ||
            u.alternateLoginEmail === null ||
            u.alternateLoginEmail.length == 0;

        return (
            powerUserMatch &&
            defaultAccessMatch &&
            enabledMatch &&
            permissionsMatch &&
            accessMatch &&
            hasGLMatch &&
            clientOwnerMatch &&
            alternateEmailMatch
        );
    }

    canExtendAccess() {
        if (!this.user || !this.clientCustomRolesNoMinMax) {
            return false;
        }

        const roles = this.clientCustomRolesNoMinMax.filter(
            (r) => r.roleName === this.user.clientCustomRoleName
        );
        return roles.length !== 0;
    }

    hasDefaultEmailChangeEvent(event) {
        this.user.hasAlternateLoginEmail = event;

        if (this.user.hasAlternateLoginEmail) {
            if (this.originalUser.hasAlternateLoginEmail) {
                this.user.alternateLoginEmail = this.originalUser.alternateLoginEmail;
            } else {
                this.user.alternateLoginEmail = null;
            }
        } else {
            this.user.alternateLoginEmail = this.user.loginEmail;
        }
    }

    // Update custom value and/or reset role name to position default
    hasDefaultAccessChangeEvent(event, clicked?: boolean) {
        if (clicked) {
            if (!event) {
                let dialogRef1 = this.dialog.open(ConfirmationAlertDialogComponent, {
                    data: {
                        title: 'Modify Hierarchy Level',
                        message: `Are you sure you want to override default access level? Clicking 'YES' will unlock ${this.selectedEmployee.value.lfName} from the hierarchy level assigned by their position.`,
                    },
                });

                dialogRef1.afterClosed().subscribe((result) => {
                    if (result === 'close') {
                        this.user.hasDefaultAccess = true;
                    } else if (result === 'accept') {
                        this.user.hasDefaultAccess = false;
                    }
                });
            } else {
                let dialogRef2 = this.dialog.open(ConfirmationAlertDialogComponent, {
                    data: {
                        title: 'Modify Hierarchy Level',
                        message: `Are you sure you want to remove hierarchy level override? Clicking 'YES' will change ${this.selectedEmployee.value.lfName} to the hierarchy level assigned by their position. Any extended access greater than their new level will be removed.`,
                    },
                });

                dialogRef2.afterClosed().subscribe((result) => {
                    if (result === 'close') {
                        this.user.hasDefaultAccess = false;
                    } else if (result === 'accept') {
                        this.user.hasDefaultAccess = true;
                        this.user.clientCustomRoleName = this.defaultPositionValues.roleName;
                    }
                });
            }
        }
    }

    // Update permission value and/or reset permission to position default
    hasDefaultPermissionsChangeEvent(event) {
        this.user.hasDefaultPermissions = event;

        if (this.originalUser.hasDefaultPermissions === this.user.hasDefaultPermissions) {
            this.user.permissionSetName = this.originalUser.permissionSetName;
        } else {
            if (this.user.hasDefaultPermissions) {
                if (!this.defaultPositionValues.permissionSetName) {
                    this.user.permissionSetName = 'None';
                } else {
                    this.user.permissionSetName = this.defaultPositionValues.permissionSetName;
                }
            }
        }
    }
    powerUserSelectChangeHandler(value: boolean) {
        if (value) {
            const dialogRef = this.dialog.open(ConfirmationAlertDialogComponent, {
                data: {
                    title: 'Power User',
                    message:
                        "Are you sure you want to give this user full access to all subscription features and all data for the organization? Clicking 'YES' will also change user's hierarchy level to Corporate, if not already.",
                },
            });

            dialogRef.afterClosed().subscribe((result) => {
                if (result === 'close') {
                    this.user.isClientAdmin = false;
                } else if (result === 'accept') {
                    this.user.hasDefaultAccess = false;
                    this.user.hasDefaultPermissions = false;
                    this.user.clientCustomRoleName = 'Corporate';
                }
            });
        } else {
            const dialogRef = this.dialog.open(ConfirmationAlertDialogComponent, {
                data: {
                    title: 'Remove as Power User',
                    message:
                        "Are you sure you want to remove power user abilities for this user? Clicking 'YES' will reset their hierarchy level and permission set to the default based on their position.",
                },
            });

            dialogRef.afterClosed().subscribe((result) => {
                if (result === 'close') {
                    this.user.isClientAdmin = true;
                } else if (result === 'accept') {
                    this.user.isClientAdmin = false;

                    this.user.hasDefaultAccess = true;
                    this.user.hasDefaultPermissions = true;

                    let defaultPositionRole = this.defaultPositionValues.roleName;
                    if (!defaultPositionRole) {
                        defaultPositionRole = this.clientCustomRoles.find(
                            (x) => x.roleName === 'Employee'
                        );

                        if (defaultPositionRole) {
                            defaultPositionRole = defaultPositionRole.roleName;
                        }
                    }

                    this.user.clientCustomRoleName = defaultPositionRole;
                    this.user.permissionSetName = this.defaultPositionValues.permissionSetName;
                }
            });
        }
    }

    glDataSelectChangeHandler(event) {
        if (event) {
            const dialogRef = this.dialog.open(ConfirmationAlertDialogComponent, {
                data: {
                    title: 'General Ledger Access',
                    message:
                        'Are you sure you want to give this user full access to General Ledger data?',
                },
            });

            dialogRef.afterClosed().subscribe((result) => {
                if (result === 'close') {
                    this.user.hasGLAccess = false;
                } else if (result === 'accept') {
                    this.user.hasGLAccess = true;
                }
            });
        }
    }
    clientOwnerSelectChangeHandler(event) {
        if (event) {
            const dialogRef = this.dialog.open(ConfirmationAlertDialogComponent, {
                data: {
                    title: 'Admin User',
                    message:
                        "Are you sure you want to give this user administrator access to Mortgage Analytics? Clicking 'YES' will also upgrade the user to Power User, granting corporate level access to all pages, features, and data.",
                },
            });

            dialogRef.afterClosed().subscribe((result) => {
                if (result === 'close') {
                    this.user.isClientOwner = false;
                } else if (result === 'accept') {
                    this.user.hasDefaultAccess = false;
                    this.user.hasDefaultPermissions = false;
                    this.user.clientCustomRoleName = 'Corporate';
                    this.user.isClientOwner = true;
                    this.user.isClientAdmin = true;
                }
            });
        } else {
            const dialogRef = this.dialog.open(ConfirmationAlertDialogComponent, {
                data: {
                    title: 'Remove as Admin User',
                    message:
                        "Are you sure you want to remove admin user abilities for this user? Clicking 'YES' will reset their hierarchy level and permission set to the default based on their position.",
                },
            });

            dialogRef.afterClosed().subscribe((result) => {
                if (result === 'close') {
                    this.user.isClientOwner = true;
                } else if (result === 'accept') {
                    this.user.isClientAdmin = false;
                    this.user.isClientOwner = false;

                    this.user.hasDefaultAccess = true;
                    this.user.hasDefaultPermissions = true;

                    let defaultPositionRole = this.defaultPositionValues.roleName;
                    if (!defaultPositionRole) {
                        defaultPositionRole = this.clientCustomRoles.find(
                            (x) => x.roleName === 'Employee'
                        );

                        if (defaultPositionRole) {
                            defaultPositionRole = defaultPositionRole.roleName;
                        }
                    }

                    this.user.clientCustomRoleName = defaultPositionRole;
                    this.user.permissionSetName = this.defaultPositionValues.permissionSetName;
                }
            });
        }
    }

    lowerHierarchyLevelChangeHandler(value: boolean) {
        try {
            if (value) {
                let originalRole = this.clientCustomRoles.find(
                    (x) => x.roleName === this.originalUser.clientCustomRoleName
                );
                let newRole = this.clientCustomRoles.find(
                    (x) => x.roleName === this.user.clientCustomRoleName
                );

                if (originalRole && newRole) {
                    if (newRole.roleOrder < originalRole.roleOrder) {
                        const dialogRef = this.dialog.open(ConfirmationAlertDialogComponent, {
                            data: {
                                title: 'Review extended access',
                                message:
                                    "Are you sure you want to lower this user's hierarchy level? Clicking 'YES' will remove any extended access assigned to a higher level.",
                            },
                        });

                        dialogRef.afterClosed().subscribe((result) => {
                            if (result === 'close') {
                                this.user.clientCustomRoleName =
                                    this.originalUser.clientCustomRoleName;
                            } else if (result === 'accept') {
                                // nothing
                            }
                        });
                    }
                }
            }
        } catch (error) {
            throw new Error(error);
        }
    }

    canSeePowerUserValues() {
        return (
            this.currentUserStateService.isLBASuperUser() ||
            this.currentUserStateService.isPowerUser() ||
            this.currentUserStateService.isPlatformUser()
        );
    }

    canSeeClientOwnerValues() {
        let hasView;

        if (this.impersonationService.isImpersonatingUser()) {
            hasView = this.impersonationService.isImpersonatingClientAdmin();
        } else {
            hasView =
                this.currentUserStateService.isLBASuperUser() ||
                this.currentUserStateService.isClientOwner();
        }

        return hasView;
    }

    openChangeClientDialog() {
        let confirmList = this.filteredEmpEmailsAvailable.filter((emp) => emp.sendEmail);

        const emailConfirmationDialogRef = this.dialog.open(EmailConfirmationDialogComponent, {
            data: { emails: confirmList },
            panelClass: 'email-confirmation-dialog-container',
        });

        emailConfirmationDialogRef.componentInstance.updateEmailsFromDialog.subscribe(
            (data: Employee) => {
                let index = this.filteredEmpEmailsAvailable.indexOf(data);
                if (index >= 0) this.filteredEmpEmailsAvailable[index] = data;
            }
        );

        emailConfirmationDialogRef.afterClosed().subscribe(
            (sendEmail: string) => {
                if (sendEmail || sendEmail.toLowerCase() === 'confirm') this.sendEmail();
                this.updateButtonAndCheckAll();
            },
            (error) => {
                throw new Error(error);
            }
        );
    }

    updateEmailsFromDialog(emails: Employee[]) {
        this.filteredEmpEmailsAvailable = emails;
    }

    exportToExcel(kendoGrid: GridComponent) {
        let dateTime = this._reusableFunctions.getCurrentDateTimeStamp();
        this.exportFileName = 'nv-user-management-' + dateTime + '_.xlsx';

        kendoGrid.excelExport.subscribe((e) => {
            e.workbook.sheets[0].columns.forEach((c) => {
                delete c.width;
                c.autoWidth = true;
            });
        });

        kendoGrid.saveAsExcel();
    }

    getExcelExportData(): ExcelExportData {
        const result: ExcelExportData = {
            data: this.exportData,
            group: [],
        };
        return result;
    }
}
